import { useEffect, useRef, useState } from "react";
import { string } from "prop-types";
import { observer } from "mobx-react";
import { onPatch } from "mobx-state-tree";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { DEFAULT_VERSION_INFO } from "@repo/utils";
import { getStructureMetadata } from "@repo/structure";
import { useEditorWebsocket, useStores } from "@hooks";
import { useFonts, useEstimateComments, useEstimateEditorStore, useLocationHash } from "@tools";
import { ProjectEditorView } from "@views/Projects/ProjectEditor/ProjectEditorView";
import { Alert, PageCircularProgress, PageCircularProgressWithLabel } from "@components";

export const ProjectEditorHandlers = observer(({ projectHash }) => {
  const { t } = useTranslation();
  const { stateStore } = useStores();
  const commentStore = useEstimateComments();
  const editorStore = useEstimateEditorStore();
  const { estimateUuid } = useParams();
  const socket = useEditorWebsocket();
  const { closeSnackbar } = useSnackbar();
  const [ searchParams ] = useSearchParams();
  const { unloadFonts } = useFonts();
  const { handleLocationHash } = useLocationHash();
  
  const [isLoading, setLoading] = useState(false);
  
  const {
    estimateLoaded,
    currentEstimateStructure,
    // estimateUsers,
    isCreated,
    showAiGenerationAlert,
  } = editorStore;
  
  // const { hasEditorPrivileges } = useCheckEstimateEditorAccess(estimateUsers);
  
  const actionListener = useRef();
  const commentActionListener = useRef();
  
  useEffect(() => {
    if(currentEstimateStructure) {
      actionListener.current?.();
      actionListener.current = onPatch(
        currentEstimateStructure,
        ({ path, value }) => {
          if(path.match(/\/historyManager\/history\/\d+/) && value?.patches)
            socket.requestStructurePatch(
              value.patches.filter(p => p.op !== "comment" && p.op !== "commentName"),
              getStructureMetadata(currentEstimateStructure),
            )
        }
      )
    }
    
    return () => {
      actionListener.current?.();
    }
  }, [currentEstimateStructure]);
  
  useEffect(() => {
    commentStore.initHistoryManager();
    commentActionListener.current?.();
    commentActionListener.current = onPatch(
      commentStore,
      ({ path, value }) => {
        if(path.match(/\/historyManager\/history\/\d+/) && value?.patches) {
          socket.requestCommentPatch(value.patches);
        }
      }
    )
    
    return () => {
      commentActionListener.current?.();
    }
  }, []);
  
  const onLoad = async () => {
    setLoading(true);
    
    let defaultVersionKey = isCreated
      ? handleLocationHash() || editorStore.sortedVersions[ 0 ].key
      : editorStore.sortedVersions[ 0 ]?.key || DEFAULT_VERSION_INFO.key;
    
    socket?.joinEstimate(
      estimateUuid,
      projectHash,
      isCreated,
      defaultVersionKey,
    );
    
    if (searchParams.get("share")) editorStore.forceShareModal(true);
    
    setLoading(false);
  };
  
  
  
  // useEffect(() => {
  //   fetchProjectComments();
  // }, [editorStore.currentEstimateStructure])
  
  useEffect(() => {
    if (estimateLoaded) onLoad();
  }, [estimateLoaded]);
  
  useEffect(() => () => {
    closeSnackbar(stateStore.lockedProjectVersionSnackbar);
    stateStore.lockedProjectVersionSnackbar = undefined;
    socket?.closeEstimate();
    
    return () => {
      unloadFonts();
      stateStore.resetProjectCreatorProgress();
    }
  }, []);
  
  if (isLoading || !currentEstimateStructure) {
    
    return <>
      {
        isCreated
          ? <PageCircularProgress />
          : <PageCircularProgressWithLabel
            value={stateStore.projectCreatorProgress}
            error={stateStore.projectCreatorProgressError}
            label={stateStore.projectCreatorProgressErrorMessage || stateStore.projectCreatorProgressStepName}
          />
      }
      {
        showAiGenerationAlert &&
        <Alert
          isOpen
          title={t("alerts.editor.ai.awaiting.title")}
        >
          {t("alerts.editor.ai.awaiting.text")}
        </Alert>
      }
    </>;
  }
  
  return <ProjectEditorView />
});

ProjectEditorHandlers.propTypes = {
  projectHash: string.isRequired,
}
